import { ensureGtagStub, GA_MEASUREMENT_ID } from './gtag';

export type AnalyticsEventParams = Record<string, string | number | boolean>;

/**
 * GA4 custom event catalog. No PII, card IDs, deck IDs, or session IDs.
 *
 * | Event | Params | Trigger |
 * |-------|--------|---------|
 * | sign_up | method: email \| google | New account created |
 * | login | method: email \| google | Returning user signs in |
 * | logout | — | User signs out |
 * | collection_add | — | Collection quantity increases |
 * | collection_remove | — | Collection quantity decreases or entry removed |
 * | collection_delete | — | Entire collection cleared |
 * | collection_export | format: csv \| xlsx, list_type: full \| missing | Collection spreadsheet download |
 * | collection_import | format, rows, matched, unrecognized | Collection CSV import applied |
 * | collection_visibility | visibility: public \| private | Collection sharing toggled |
 * | deck_create | — | New deck saved |
 * | deck_save | — | Existing deck updated |
 * | deck_publish | visibility: public \| unlisted \| private | Deck published to gallery |
 * | deck_delete | — | Deck deleted |
 * | deck_duplicate | — | Deck duplicated |
 * | deck_export | format: tcgarena \| riftatlas \| pdf \| image \| deck_code | Deck list exported |
 * | deck_import | format: deck_code \| deck_list, missing | Deck code or list imported into builder |
 * | match_start | format, mode: match \| event | Shared match session or event round started |
 * | match_join | — | User joins shared match |
 * | match_complete | mode: match \| event | Shared or local match finalized |
 * | match_save | source: auto \| manual \| quick_log, mode: match \| event | Local or shared match persisted |
 * | event_start | format, event_type | Nexus Night / tournament created |
 * | event_complete | format, event_type, rounds | Event finished (poster / recap) |
 * | match_undo_save | — | User removed an auto-saved match |
 * | match_play_again | — | User started a new match after complete |
 * | scan_commit | — | Scanner card confirmed to collection |
 * | search | search_term (optional) | User changes search term or filters |
 * | proxy_download | — | Proxy PDF downloaded |
 * | pwa_install | — | PWA install prompt accepted |
 * | account_delete | — | Account deleted |
 * | riftboundle_complete | outcome: won \| lost | Daily puzzle finished |
 */
export const AnalyticsEvents = {
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  COLLECTION_ADD: 'collection_add',
  COLLECTION_REMOVE: 'collection_remove',
  COLLECTION_DELETE: 'collection_delete',
  COLLECTION_EXPORT: 'collection_export',
  COLLECTION_IMPORT: 'collection_import',
  COLLECTION_VISIBILITY: 'collection_visibility',
  DECK_CREATE: 'deck_create',
  DECK_SAVE: 'deck_save',
  DECK_PUBLISH: 'deck_publish',
  DECK_DELETE: 'deck_delete',
  DECK_DUPLICATE: 'deck_duplicate',
  DECK_EXPORT: 'deck_export',
  DECK_IMPORT: 'deck_import',
  MATCH_START: 'match_start',
  MATCH_JOIN: 'match_join',
  MATCH_COMPLETE: 'match_complete',
  MATCH_SAVE: 'match_save',
  MATCH_UNDO_SAVE: 'match_undo_save',
  MATCH_PLAY_AGAIN: 'match_play_again',
  EVENT_START: 'event_start',
  EVENT_COMPLETE: 'event_complete',
  SCAN_COMMIT: 'scan_commit',
  SEARCH: 'search',
  PROXY_DOWNLOAD: 'proxy_download',
  PWA_INSTALL: 'pwa_install',
  ACCOUNT_DELETE: 'account_delete',
  RIFTBOUNDLE_COMPLETE: 'riftboundle_complete',
} as const;

/**
 * Send a GA4 event. No-op when `VITE_GA_MEASUREMENT_ID` is unset.
 */
export function trackEvent(name: string, params?: AnalyticsEventParams): void {
  if (!GA_MEASUREMENT_ID) return;
  ensureGtagStub();
  const cleaned = params
    ? Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined),
      )
    : undefined;
  window.gtag!('event', name, cleaned);
}
